import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, MapPinIcon, MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { UniversityListing } from '../types/listings';
interface UniversityListingCardProps {
  listing: UniversityListing;
}

/**
 * A single advertiser listing on a guide page, built to read as a comparison
 * result rather than a display ad.
 *
 * The WHOLE card is the link, not just the button: on a phone the entire block
 * is one large tap target, and the orange button is a visual affordance inside
 * it rather than a second link. That is also why there is exactly one
 * interactive element per listing — nested links would be invalid and would give
 * keyboard and screen-reader users two stops for one destination.
 *
 * Attention order is deliberate: logo and badge, then school name, then the
 * value-proposition headline, then the reasons to click. Accreditation and
 * location are present for trust but deliberately quiet.
 */
export function UniversityListingCard({
  listing
}: UniversityListingCardProps) {
  const {
    rank,
    schoolName,
    location,
    accreditation,
    topBadge,
    headline,
    stats,
    benefits,
    programs,
    logo,
    ctaText,
    ctaUrl,
    ctaSubtext,
    featured
  } = listing;
  const isInternal = ctaUrl.startsWith('/');
  const shellClasses = `group block bg-white transition-shadow duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cta-dark ${featured ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg hover:shadow-xl' : 'rounded-xl border border-gray-200 shadow-sm hover:shadow-md'}`;
  const badgePill = topBadge ? <span className="inline-flex items-center justify-center text-center bg-light border border-gray-200 text-navy text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-full" data-id="element-998" data-mp="3ohzml0pxlf9t">
      {topBadge}
    </span> : null;
  const content = <div className="p-4 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:gap-8" data-id="element-999" data-mp="1u72zbya6eqaf">
      {/* Main content */}
      <div className="flex-1 min-w-0" data-id="element-1000" data-mp="2xi0a7wc2uq5n">
        {/* Narrow-screen header: rank and qualifier badge on one line, then a
            large centred logo. On desktop the rank moves inline beside the name
            and the logo moves to the right column. */}
        <div className="lg:hidden" data-id="element-1001" data-mp="32eh12yanvimf">
          <div className="flex items-center justify-between gap-3" data-id="element-1002" data-mp="32hmq6djaiki5">
            <span className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-bold ${featured ? 'bg-navy text-white' : 'border border-gray-200 text-gray-500'}`} data-id="element-1003" data-mp="3gdgndzapblff">
              #{rank}
              <span className="sr-only" data-id="element-1004" data-mp="3l6jk2garzazu"> ranked</span>
            </span>
            {badgePill}
          </div>

          <div className="mt-5 mb-6 flex items-center justify-center" data-id="element-1005" data-mp="2y29hegum9nr">
            <img src={logo} alt={`${schoolName} logo`} className="h-16 sm:h-20 w-auto max-w-[240px] object-contain" loading="lazy" data-id="element-1006" data-mp="3u7r35r4q059c" />
          </div>
        </div>

        <div className="flex gap-3 sm:gap-4" data-id="element-1007" data-mp="mju8ymc842v9">
          <div className="hidden lg:block flex-shrink-0 pt-0.5" data-id="element-1008" data-mp="8mxabypv5h3t">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${featured ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-1009" data-mp="cnqzevf1l1ya">
              <span className="text-sm font-bold" data-id="element-1010" data-mp="3p5kda8s4529">{rank}</span>
              <span className="sr-only" data-id="element-1011" data-mp="3l4lq7cikevw0">Ranked number {rank}</span>
            </div>
          </div>

          <div className="flex-1 min-w-0" data-id="element-1012" data-mp="3q3owf0aa785k">
            {/* Identity */}
            <h3 className="text-xl sm:text-2xl font-bold text-navy leading-tight text-center lg:text-left" data-id="element-1013" data-mp="3c34b9xgw908w">
              {schoolName}
            </h3>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center justify-center lg:justify-start gap-x-3 sm:gap-x-4 gap-y-1 text-xs text-gray-500" data-id="element-1014" data-mp="3h15wop1slbnj">
              <span className="inline-flex items-center gap-1.5" data-id="element-1015" data-mp="341ogsw220isb">
                <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" data-id="element-1016" data-mp="344izoj5f5vjm" />
                {location}
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-1017" data-mp="2v7s407us4ipk">
                <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-1018" data-mp="2qbky2f2dv77m" />
                {accreditation}
              </span>
            </div>

            {/* The reason to click. Desktop only — on a phone the bullets carry
                the pitch and this line only pushes the button down. */}
            <p className="hidden lg:block mt-4 text-lg font-bold leading-snug" style={{
            color: '#332B66'
          }} data-id="element-1019" data-mp="elincvlsn5bo">
              {headline}
            </p>

            {/* Stats. Held back on narrow screens: the benefit bullets say the
                same things in plainer language, and the tiles are the tallest
                block in the card. */}
            <dl className="hidden lg:grid mt-4 grid-cols-3 gap-3" data-id="element-1020" data-mp="226k1qw6go6dh">
              {stats.map(stat => <div key={stat.label} className="bg-light border border-gray-200 rounded-lg px-2 py-2.5 sm:px-3 sm:py-3 text-center" data-id="element-1021" data-mp="268x5xg5k7w85">
                  <dt className="sr-only" data-id="element-1022" data-mp="1egzgg6mu4x11">{stat.label}</dt>
                  <dd data-id="element-1023" data-mp="1ejln841wj8i7">
                    <span className="block text-base sm:text-lg font-bold text-navy leading-tight" data-id="element-1024" data-mp="1ij2vt6cmklrl">
                      {stat.value}
                    </span>
                    <span className="block mt-1 text-[11px] sm:text-xs text-gray-600 leading-tight" data-id="element-1025" data-mp="1njh1ihqr7jmg">
                      {stat.label}
                    </span>
                  </dd>
                </div>)}
            </dl>

            {/* Benefits */}
            <ul className="mt-4 sm:mt-5 space-y-2.5 text-left" data-id="element-1026" data-mp="yu1u1ty26vaw">
              {benefits.map(benefit => <li key={benefit} className="flex items-start gap-2.5" data-id="element-1027" data-mp="usq0jdc2dpxf">
                  <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-1028" data-mp="18tspbxug1ec1" />
                  <span className="text-sm text-gray-700 leading-relaxed" data-id="element-1029" data-mp="13sd3w4oct5t4">
                    {benefit}
                  </span>
                </li>)}
            </ul>

            {/* Programs */}
            {programs.length > 0 && <div className="hidden lg:block mt-5 pt-5 border-t border-gray-100" data-id="element-1030" data-mp="pvge86jz1udt">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2.5" data-id="element-1031" data-mp="l36l6hnk8e7b">
                  Popular Online Programs
                </p>
                <ul className="flex flex-wrap gap-2" data-id="element-1032" data-mp="3th5bb2nup5cw">
                  {programs.map(program => <li key={program} className="text-xs font-medium text-gray-700 bg-light border border-gray-200 rounded-full px-3 py-1.5" data-id="element-1033" data-mp="3oikkftuhny14">
                      {program}
                    </li>)}
                </ul>
              </div>}
          </div>
        </div>
      </div>

      {/* Call to action. A styled span, not a link — the card itself carries the
          destination. */}
      <div className="mt-4 pt-4 border-t border-gray-100 sm:mt-6 sm:pt-6 lg:mt-0 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 lg:w-60 lg:flex-shrink-0 flex flex-col items-center justify-center gap-3 sm:gap-4" data-id="element-1034" data-mp="2mqrhx7gjxd4f">
        <span className="hidden lg:inline-flex" data-id="element-1035" data-mp="2duii3ronwchr">{badgePill}</span>

        <div className="hidden lg:flex w-full h-28 items-center justify-center" data-id="element-1036" data-mp="2ippgql32qqzt">
          <img src={logo} alt="" aria-hidden="true" className="max-w-full max-h-full object-contain" loading="lazy" data-id="element-1037" data-mp="12bzap8se9snj" />
        </div>

        <span className="flex items-center justify-center text-center w-full min-h-[52px] sm:min-h-[60px] px-6 py-3.5 sm:py-4 text-base font-bold rounded-xl text-white bg-cta shadow-md group-hover:bg-cta-dark group-hover:shadow-lg transition-[background-color,box-shadow] duration-200 ease-out" data-id="element-1038" data-mp="1y38dcz4otcyb">
          {ctaText}
        </span>

        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-1039" data-mp="1k48ts5w17o90">
          <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-1040" data-mp="2hpd5517jj22a" />
          {ctaSubtext}
        </span>
      </div>
    </div>;
  return <article className="relative" data-id="element-1041" data-mp="27yik206168if">
      {featured && <div className="rounded-t-xl px-4 py-2 sm:py-3 flex items-center justify-center gap-2" style={{
      backgroundColor: '#5FA396'
    }} data-id="element-1042" data-mp="2321ssx3jbe14">
          <TrophyIcon className="w-4 h-4 text-gold-light" aria-hidden="true" data-id="element-1043" data-mp="2vpxp40p6z3xd" />
          <span className="text-white text-sm font-bold tracking-wide" data-id="element-1044" data-mp="2vnuoynubruex">
            #1 Recommended School
          </span>
        </div>}

      {isInternal ? <Link to={ctaUrl} className={shellClasses} aria-label={`${ctaText} — ${schoolName}`} data-id="element-1045" data-mp="380yexbn53ryu">
          {content}
        </Link> : <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className={shellClasses} aria-label={`${ctaText} — ${schoolName} (opens in a new tab)`} data-id="element-1046" data-mp="3hymnb4do4xzh">
          {content}
        </a>}
    </article>;
}